/**
 * Single source of truth for supported UI languages.
 * To add a new language:
 * 1. Add its code to SUPPORTED_LANGUAGE_CODES
 * 2. Add backend enum id to BACKEND_LANGUAGE_IDS (if used for auth/API)
 * 3. Add translation keys in i18n.js: languages.xx and all namespaced content
 * 4. Add date adapter locale in components that use date pickers (e.g. TeamMemberPersonalInfo)
 */

import { hasFunctionalConsent } from '../utils/cookieConsentStorage';
import { parseMarketingPath } from './marketingSeo';

export const SUPPORTED_LANGUAGE_CODES = ['en', 'pt', 'es'];

export const DEFAULT_LANGUAGE = 'pt';

/** Backend Language enum used by auth/API (e.g. customer login). Add new languages here when backend supports them. */
export const BACKEND_LANGUAGE_IDS = {
  pt: 1,
  en: 2,
  es: 3,
};

const BACKEND_LANGUAGE_CODES = {
  1: 'pt',
  2: 'en',
  3: 'es',
};

/**
 * Normalize any language code to a supported one (e.g. 'en-US' -> 'en').
 * @param {string|number} lng - Raw language code from i18n, localStorage, or API
 * @returns {string} One of SUPPORTED_LANGUAGE_CODES, or DEFAULT_LANGUAGE
 */
export function normalizeLanguage(lng) {
  if (lng == null) return DEFAULT_LANGUAGE;

  if (typeof lng === 'number') {
    return BACKEND_LANGUAGE_CODES[lng] ?? DEFAULT_LANGUAGE;
  }

  if (typeof lng !== 'string') return DEFAULT_LANGUAGE;

  const trimmed = lng.trim();
  if (!trimmed) return DEFAULT_LANGUAGE;

  if (/^\d+$/.test(trimmed)) {
    return BACKEND_LANGUAGE_CODES[Number(trimmed)] ?? DEFAULT_LANGUAGE;
  }

  const lower = trimmed.toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGUAGE_CODES.includes(lower) ? lower : DEFAULT_LANGUAGE;
}

/**
 * Preferred language from localStorage, normalized. Use for initial load / API calls.
 */
export function getPreferredLanguage() {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  if (!hasFunctionalConsent()) {
    return DEFAULT_LANGUAGE;
  }

  const saved = localStorage.getItem('preferred-language');
  return normalizeLanguage(saved || DEFAULT_LANGUAGE);
}

/**
 * Language for the first render: a language-prefixed marketing URL (`/en/...`) wins so
 * every localized URL renders its own language, otherwise the stored preference.
 */
export function getInitialLanguage() {
  if (typeof window !== 'undefined') {
    const { lang, prefixed } = parseMarketingPath(window.location.pathname);
    if (prefixed) {
      return lang;
    }
  }

  return getPreferredLanguage();
}

/**
 * Backend language enum value for the given UI language code.
 * @param {string} code - UI language code (e.g. 'en', 'pt')
 * @returns {number} Backend enum value, or EN as fallback
 */
export function getBackendLanguageId(code) {
  const normalized = normalizeLanguage(code);
  return BACKEND_LANGUAGE_IDS[normalized] ?? BACKEND_LANGUAGE_IDS[DEFAULT_LANGUAGE];
}

function getLocalizedValueForLanguage(values, code) {
  if (!values || typeof values !== 'object') return null;

  const normalized = normalizeLanguage(code);
  const backendId = BACKEND_LANGUAGE_IDS[normalized];
  const candidates = [normalized.toUpperCase(), normalized, String(backendId)];

  for (const candidate of candidates) {
    const value = values[candidate];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return null;
}

export function getLocalizedValue(values, preferredLanguage, fallbackLanguage, finalFallback = '') {
  const languageCandidates = [preferredLanguage, fallbackLanguage, DEFAULT_LANGUAGE]
    .map((code) => normalizeLanguage(code))
    .filter((code, index, array) => array.indexOf(code) === index);

  for (const candidate of languageCandidates) {
    const value = getLocalizedValueForLanguage(values, candidate);
    if (value) {
      return value;
    }
  }

  const firstAvailableValue = Object.values(values || {}).find(
    (value) => typeof value === 'string' && value.trim()
  );

  return firstAvailableValue || finalFallback;
}

/**
 * Optional: language options with flags for store registration / service forms.
 * Display names should come from i18n (t('languages.xx')) in the component.
 */
export const LANGUAGE_FLAGS = {
  pt: '🇵🇹',
  en: '🇬🇧',
  es: '🇪🇸',
};

export const LANGUAGE_COUNTRY_CODES = {
  pt: 'PT',
  en: 'GB',
  es: 'ES',
};

/** Default supported languages for new store registration (can be overridden per store). */
export const DEFAULT_STORE_SUPPORTED_LANGUAGES = [DEFAULT_LANGUAGE];
